import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  addYears,
  daysFromNow,
  endOfMonth,
  getAmsterdamHours,
  isAfter,
  isBefore,
  isEqual,
  isToday,
  isTodayAmsterdam,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from '@eneco/common/date';
import { TransformedSlice } from '@eneco/hooks/dynamicPricing';
import { DC_Domain_Models_Usages_UsageAggregation, DC_Domain_Models_Usages_UsageInterval } from '@monorepo-types/dc';

import { GraphData, GraphViewOption } from './types';

export const getStartDate = (view: GraphViewOption) => {
  const currentDate = new Date();
  switch (view) {
    case 'day':
      return startOfDay(currentDate);
    case 'week':
      return startOfWeek(currentDate, { weekStartsOn: 1 });
    case 'month':
      return startOfMonth(currentDate);
    case 'year':
      return startOfYear(currentDate);
    default:
      return currentDate;
  }
};

export const shiftDate = (view: GraphViewOption, date: Date, offset: number) => {
  switch (view) {
    case 'day':
      return addDays(date, offset);
    case 'week':
      return addWeeks(date, offset);
    case 'month':
      return addMonths(date, offset);
    case 'year':
      return addYears(date, offset);
    default:
      return date;
  }
};

export const getTickCount = (view: GraphViewOption, date = new Date()) => {
  switch (view) {
    case 'day':
      return 24;
    case 'week':
      return 7;
    case 'month':
      return endOfMonth(date).getDate();
    case 'year':
      return 12;
    default:
      return 24;
  }
};

export const requestConfigurationMap: Record<
  GraphViewOption,
  {
    interval: DC_Domain_Models_Usages_UsageInterval;
    aggregation: DC_Domain_Models_Usages_UsageAggregation;
  }
> = {
  day: {
    interval: 'Hour',
    aggregation: 'Day',
  },
  week: {
    interval: 'Day',
    aggregation: 'Week',
  },
  month: {
    interval: 'Day',
    aggregation: 'Month',
  },
  year: {
    interval: 'Month',
    aggregation: 'Year',
  },
};

export const toGraphData = (slice: TransformedSlice): GraphData => ({
  total: slice.price?.totalVat?.inclVat ?? Number.NaN,
  wholesale: slice.price?.components?.find(priceComponent => priceComponent.type === 'wholesale')?.cost ?? Number.NaN,
  commercial: slice.price?.components?.find(priceComponent => priceComponent.type === 'commercial')?.cost ?? Number.NaN,
  energytax: slice.price?.components?.find(priceComponent => priceComponent.type === 'energyTax')?.cost ?? Number.NaN,
  start: slice.start,
  rating: slice.rating,
  isCurrentHour: slice.isCurrentHour,
});

export const splitDataIntoSections = (data: GraphData[], selectedView: Exclude<GraphViewOption, 'week'>) => {
  const sections = {
    day: 4,
    month: 5,
    year: 2,
  };
  const sectionSize = Math.ceil(data.length / sections[selectedView]);
  const formattedData: GraphData[][] = [];
  for (let i = 0; i < data.length; i += sectionSize) {
    formattedData.push(data.slice(i, i + sectionSize));
  }
  return formattedData;
};

export const getGraphData = (
  selectedView: GraphViewOption,
  start: Date | undefined,
  productData: TransformedSlice[] | undefined,
) => {
  //DC doesn't provide data for missing entries, so we need to fill them with empty data
  const data = Array.from({ length: getTickCount(selectedView, start) }, (_, index) => {
    const date = incrementDate(selectedView, start || new Date(), index);
    const slice = productData?.find(slice => isEqual(slice.start, date)) || {
      start: date,
      isCurrentHour: false,
      rating: 'unknown',
      price: {
        total: 0,
        rating: 'unknown',
      },
    };
    return toGraphData(slice);
  });
  return data;
};

const isItemInCurrentView = (item: GraphData, view: GraphViewOption, date: Date) => {
  if (view === 'day') {
    return item.start.getHours() === date.getHours() && isToday(item.start);
  }
  return isAfter(item.start, date);
};

export const getDefaultSelectedSection = (formattedData: GraphData[][], selectedView: GraphViewOption) => {
  const currentDate = new Date();
  const sectionIndex = formattedData.findIndex(section =>
    section.some(item => isItemInCurrentView(item, selectedView, currentDate)),
  );
  return sectionIndex === -1 ? 0 : sectionIndex;
};

export const getBarWidth = (view: GraphViewOption, isDesktop: boolean) => {
  switch (view) {
    case 'day':
      return isDesktop ? 16 : 18;
    case 'week':
      return isDesktop ? 28 : 18;
    case 'month':
      return isDesktop ? 12 : 18;
    case 'year':
    default:
      return isDesktop ? 28 : 18;
  }
};

export const incrementDate = (view: GraphViewOption, date: Date, amount: number) => {
  switch (view) {
    case 'day':
      return addHours(date, amount);
    case 'week':
      return addDays(date, amount);
    case 'month':
      return addDays(date, amount);
    case 'year':
    default:
      return addMonths(date, amount);
  }
};

export const shouldShowNext = (selectedDate: Date, selectedView: GraphViewOption, isDesktop: boolean, sections = 0) => {
  const now = new Date();

  if (selectedView === 'day') {
    const isSelectedDateInPast =
      selectedDate.getFullYear() < now.getFullYear() ||
      selectedDate.getMonth() < now.getMonth() ||
      selectedDate.getDate() < now.getDate();

    const isCurrentTimeAfter13PM = getAmsterdamHours(now) >= 13;
    const nextDateSection = addHours(selectedDate, 6);
    const isNextDateSectionWithinTwoDaysFromNow = isBefore(nextDateSection, daysFromNow(2));
    const isNextDateSectionToday = isTodayAmsterdam(nextDateSection);

    const showNextDesktop = isTodayAmsterdam(selectedDate) && isCurrentTimeAfter13PM;
    const showNextMobile = (isCurrentTimeAfter13PM && isNextDateSectionWithinTwoDaysFromNow) || isNextDateSectionToday;

    return isSelectedDateInPast || (isDesktop ? showNextDesktop : showNextMobile);
  }
  if (selectedView === 'week') {
    return addDays(selectedDate, 7) <= now;
  }
  if (selectedView === 'month') {
    return isDesktop ? addMonths(selectedDate, 1) <= now : addDays(selectedDate, sections) <= now;
  }
  if (selectedView === 'year') {
    return isDesktop ? addYears(selectedDate, 1) <= now : addMonths(selectedDate, 6) <= now;
  }
  return false;
};
